import { useSubscribe } from "@booking/hooks/usePubSub";
import { closeDialog } from "@booking/utils/helpers";
import { FILTER_CONTEXT_LABELS, FILTER_CONTEXTS } from "@search/api/constants";
import type { SearchCounts } from "@search/api/types";
import { CheckboxFacet } from "@search/components/CheckboxFacet";
import DatePickerFacet from "@search/components/DatePickerFacet";
import DropdownFacet from "@search/components/DropdownFacet";
import LocationAutocompleteFacet from "@search/components/LocationAutocompleteFacet";
import LocationFacets from "@search/components/LocationFacets";
import RegionFacets from "@search/components/RegionFacets";
import SimplePriceFacet from "@search/components/SimplePriceFacet";
import ClearFilter from "@search/components/molecules/ClearFilter";
import StickyFooter from "@search/components/molecules/StickyFooter";
import { FilterSet } from "@search/store/FilterSet";
import { Filters } from "@search/store/types";
import clsx from "clsx";
import pluralize from "pluralize";
import { useState } from "react";
import React from "react";
import { useNavigate } from "react-router-dom";

import { CheckboxInputEventEnum } from "@/components/compounds/forms/CheckboxInput";
import { DropdownInputEventEnum } from "@/components/compounds/forms/DropdownInput";
import type { CommonProps, PubSubData } from "@/types";

import styles from "./FilterList.module.css";

const ref = "filter-list";

interface FiltersProps extends Partial<CommonProps> {
  counts: SearchCounts;
  filters: Filters;
  isLoading: boolean;
  totalResults: number;
}

function useFilters(props: FiltersProps) {
  const { counts, filters, isLoading, totalResults } = props;
  const className = clsx(styles[ref], props.className);

  return {
    className,
    counts,
    filterSet: new FilterSet(filters),
    isLoading,
    totalResults,
  };
}

function FilterList(props: FiltersProps) {
  const { className, counts, isLoading, filterSet, totalResults } =
    useFilters(props);
  const navigate = useNavigate();

  const boardBasisContexts = counts.boardBasis.map((bb) => bb.initial);

  function updateControl(
    data: PubSubData & {
      context: typeof boardBasisContexts;
    },
  ) {
    const newQuery = filterSet.applyBoardBasis(data.context, data.value);

    navigate(newQuery);
  }

  function updateDate(date: Date) {
    const newQuery = filterSet.applyDate(date);

    navigate(newQuery);
  }

  function updateRegion(id: number) {
    const newQuery = filterSet.applyRegion(id);

    navigate(newQuery);
  }

  function updateDistance(distance: number) {
    const newQuery = filterSet.applyDistance(distance);

    navigate(newQuery);
  }

  function updateLocation(slug: string) {
    const newQuery = filterSet.applyLocation(slug);

    navigate(newQuery);
  }

  function updateRadioControl(data: PubSubData) {
    switch (data.context) {
      case FILTER_CONTEXTS.available_book_online: {
        const newQuery = filterSet.applyBookOnline(data.value);

        navigate(newQuery);

        break;
      }
      case FILTER_CONTEXTS.eurotunnel: {
        const newQuery = filterSet.applyEurotunnel(data.value);

        navigate(newQuery);

        break;
      }
      case FILTER_CONTEXTS.flights: {
        const newQuery = filterSet.applyFlights(data.value);

        navigate(newQuery);

        break;
      }
      case FILTER_CONTEXTS.four_ball: {
        const newQuery = filterSet.applyFourBall(data.value);

        navigate(newQuery);

        break;
      }
      case FILTER_CONTEXTS.extra_yards: {
        const newQuery = filterSet.applyExtraYards(data.value);

        navigate(newQuery);

        break;
      }
      case FILTER_CONTEXTS.online_exclusive: {
        const newQuery = filterSet.applyOnlineExclusive(data.value);

        navigate(newQuery);

        break;
      }
      case FILTER_CONTEXTS.reviews: {
        const newQuery = filterSet.applyReviews(data.value);

        navigate(newQuery);

        break;
      }
      case FILTER_CONTEXTS.top_picks: {
        const newQuery = filterSet.applyTopPicks(data.value);

        navigate(newQuery);

        break;
      }
      case FILTER_CONTEXTS.tours: {
        const newQuery = filterSet.applyTours(data.value);

        navigate(newQuery);

        break;
      }
    }
  }

  function updateDropDownControl(data: PubSubData) {
    switch (data.context) {
      case FILTER_CONTEXTS.nights: {
        const newNightsQuery = filterSet.applyNights(parseInt(data.value));

        navigate(newNightsQuery);

        break;
      }
      case FILTER_CONTEXTS.rounds: {
        const newRoundsQuery = filterSet.applyRounds(parseInt(data.value));

        navigate(newRoundsQuery);

        break;
      }
    }
  }

  function clearRegion() {
    const newQuery = filterSet.clearRegion();

    navigate(newQuery);
  }

  function clearLocation() {
    const newQuery = filterSet.clearLocation();

    navigate(newQuery);
  }

  function clearNightsAndRounds() {
    const newQuery = filterSet.clearNightsAndRounds();

    navigate(newQuery);
  }

  function clearBoardBasis() {
    const newQuery = filterSet.clearBoardBasis();

    navigate(newQuery);
  }

  function clearDate() {
    const newQuery = filterSet.clearDate();

    navigate(newQuery);
  }

  const [userMax, setUserMax] = useState<number>(counts.price.max);
  const [userMin, setUserMin] = useState<number>(counts.price.min);
  const priceRange = filterSet.getPrice();
  const currentMax = priceRange[1] || userMax || counts.price.max;
  const currentMin = priceRange[0] || userMin || counts.price.min;

  function updateMax(e: React.ChangeEvent<HTMLInputElement>) {
    setUserMax(parseInt(e.target.value, 10));
  }

  function updateMin(e: React.ChangeEvent<HTMLInputElement>) {
    setUserMin(parseInt(e.target.value, 10));
  }

  function clearPrice() {
    const newQuery = filterSet.clearPrice();

    navigate(newQuery);
  }

  function updatePrice() {
    const price = [currentMin, currentMax];
    const newQuery = filterSet.applyPrice(price);

    navigate(newQuery);
  }

  useSubscribe(
    CheckboxInputEventEnum.CHANGE,
    updateControl,
    boardBasisContexts,
  );
  useSubscribe(CheckboxInputEventEnum.CHANGE, updateRadioControl);
  useSubscribe(
    DropdownInputEventEnum.SELECTED_INDEX_CHANGE,
    updateDropDownControl,
  );

  return (
    <section className={className}>
      <h2>Filter by:</h2>

      <fieldset>
        <CheckboxFacet
          key={"rd_abo"}
          checked={filterSet.getBookOnline()}
          className={"radio-checkbox"}
          count={counts.bookOnline}
          eventGroup={"Filters"}
          gaClick={"book_online_click"}
          id={FILTER_CONTEXTS.available_book_online}
          label={FILTER_CONTEXT_LABELS[FILTER_CONTEXTS.available_book_online]}
          name={FILTER_CONTEXTS.available_book_online}
        />
        {/* <CheckboxFacet
          key={"rd_oe"}
          checked={filterSet.getOnlineExclusive()}
          className={"radio-checkbox"}
          count={counts.onlineExclusive}
          eventGroup={"Filters"}
          gaClick={"online_exclusive_click"}
          id={FILTER_CONTEXTS.online_exclusive}
          label={FILTER_CONTEXT_LABELS[FILTER_CONTEXTS.online_exclusive]}
          name={FILTER_CONTEXTS.online_exclusive}
        /> */}
        <CheckboxFacet
          key={"rd_tp"}
          checked={filterSet.getTopPicks()}
          className={"radio-checkbox"}
          count={counts.topPicks}
          eventGroup={"Filters"}
          gaClick={"top_picks_click"}
          id={FILTER_CONTEXTS.top_picks}
          label={FILTER_CONTEXT_LABELS[FILTER_CONTEXTS.top_picks]}
          name={FILTER_CONTEXTS.top_picks}
        />
        <CheckboxFacet
          key={"rd_ey"}
          checked={filterSet.getExtraYards()}
          className={"radio-checkbox"}
          count={counts.extraYards}
          eventGroup={"Filters"}
          gaClick={"extra_yards_click"}
          id={FILTER_CONTEXTS.extra_yards}
          label={FILTER_CONTEXT_LABELS[FILTER_CONTEXTS.extra_yards]}
          name={FILTER_CONTEXTS.extra_yards}
        />
        {/* <CheckboxFacet
          key={"rd_fb"}
          checked={filterSet.getFourBall()}
          className={"radio-checkbox"}
          count={counts.fourBall}
          eventGroup={"Filters"}
          gaClick={"four_ball_click"}
          id={FILTER_CONTEXTS.four_ball}
          label={FILTER_CONTEXT_LABELS[FILTER_CONTEXTS.four_ball]}
          name={FILTER_CONTEXTS.four_ball}
        /> */}
        <CheckboxFacet
          key={"rd_to"}
          checked={filterSet.getTours()}
          className={"radio-checkbox"}
          count={counts.tours}
          eventGroup={"Filters"}
          gaClick={"tours_click"}
          id={FILTER_CONTEXTS.tours}
          label={FILTER_CONTEXT_LABELS[FILTER_CONTEXTS.tours]}
          name={FILTER_CONTEXTS.tours}
        />
      </fieldset>

      <fieldset>
        <h3>Location</h3>
        <LocationAutocompleteFacet
          updateRegion={updateRegion}
          updateLocation={updateLocation}
        />

        {counts.regions && (
          <RegionFacets regions={counts.regions} update={updateRegion} />
        )}

        {filterSet.regionFilter.isSet() && (
          <ClearFilter name={"clear_region"} updater={clearRegion} />
        )}

        {counts.locations && (
          <LocationFacets
            locations={counts.locations}
            update={updateDistance}
          />
        )}

        {filterSet.locationFilter.isSet() && (
          <ClearFilter name={"clear_location"} updater={clearLocation} />
        )}
      </fieldset>

      <fieldset>
        <h3>Nights</h3>
        <DropdownFacet
          eventGroup={"Filters"}
          hideLabel
          id={FILTER_CONTEXTS.nights}
          label={FILTER_CONTEXT_LABELS[FILTER_CONTEXTS.nights]}
          name={FILTER_CONTEXTS.nights}
          options={filterSet.nightFilter.options(counts.nights)}
          selectedValue={filterSet.getNights().toString()}
        />

        <h3>Rounds</h3>
        <DropdownFacet
          eventGroup={"Filters"}
          hideLabel
          id={FILTER_CONTEXTS.rounds}
          label={FILTER_CONTEXT_LABELS[FILTER_CONTEXTS.rounds]}
          name={FILTER_CONTEXTS.rounds}
          options={filterSet.roundFilter.options(counts.rounds)}
          selectedValue={filterSet.getRounds().toString()}
        />

        {(filterSet.nightFilter.isSet() || filterSet.roundFilter.isSet()) && (
          <ClearFilter
            name={"clear_nights_and_rounds"}
            updater={clearNightsAndRounds}
          />
        )}
      </fieldset>

      <fieldset>
        <h3>Arrival date</h3>
        <DatePickerFacet update={updateDate} date={filterSet.getDate()} />
        {filterSet.dateFilter.isSet() && (
          <ClearFilter name={"clear_date"} updater={clearDate} />
        )}
      </fieldset>

      <fieldset>
        <h3>Price</h3>
        <SimplePriceFacet
          id={FILTER_CONTEXTS.price}
          min={counts.price.min}
          max={counts.price.max}
          name={FILTER_CONTEXTS.price}
          values={[currentMin, currentMax]}
          updateMin={updateMin}
          updateMax={updateMax}
          update={updatePrice}
        />
        {filterSet.priceFilter.isSet() && (
          <ClearFilter name={"clear_price"} updater={clearPrice} />
        )}
      </fieldset>

      <fieldset>
        <h3>Board basis</h3>
        {counts.boardBasis.map(({ name, initial, count }, index) => (
          <CheckboxFacet
            key={`bb_${initial}`}
            checked={filterSet.includesBoardBasis(initial)}
            count={count}
            gaClick={"board_basis_click"}
            id={`board_basis_${index}`}
            label={name}
            name={initial}
          />
        ))}
        {filterSet.boardBasisFilter.isSet() && (
          <ClearFilter name={"clear_board_basis"} updater={clearBoardBasis} />
        )}
      </fieldset>

      <fieldset>
        <h3>Reviews</h3>
        <CheckboxFacet
          checked={filterSet.getReviews()}
          count={counts.reviews}
          id={FILTER_CONTEXTS.reviews}
          label={FILTER_CONTEXT_LABELS[FILTER_CONTEXTS.reviews]}
          name={FILTER_CONTEXTS.reviews}
        />
      </fieldset>

      <fieldset>
        <h3>Transport Options</h3>
        <CheckboxFacet
          key={"rd_te"}
          checked={filterSet.getEurotunnel()}
          className={"radio-checkbox"}
          count={counts.transportation.eurotunnel}
          eventGroup={"Filters"}
          gaClick={"eurotunnel_click"}
          id={FILTER_CONTEXTS.eurotunnel}
          label={FILTER_CONTEXT_LABELS[FILTER_CONTEXTS.eurotunnel]}
          name={FILTER_CONTEXTS.eurotunnel}
        />
        <CheckboxFacet
          key={"rd_tf"}
          checked={filterSet.getFlights()}
          className={"radio-checkbox"}
          count={counts.transportation.flights}
          eventGroup={"Filters"}
          gaClick={"flights_click"}
          id={FILTER_CONTEXTS.flights}
          label={FILTER_CONTEXT_LABELS[FILTER_CONTEXTS.flights]}
          name={FILTER_CONTEXTS.flights}
        />
      </fieldset>

      <StickyFooter
        actionText={`View ${pluralize("Result", totalResults, true)}`}
        isLoading={isLoading}
        onClick={closeDialog}
      />
    </section>
  );
}

export default FilterList;
export { FilterList };
